import {
  formatCollectionActivityClock,
  formatCollectionActivityTime,
} from './collectionActivityDisplay';
import { getInquiryDisplayAt, getManualInquiryTags, isManualInquiry } from '../../Leads/leadsConfig';

export const INQUIRY_SEEN_FILTER_OPTIONS = [
  { id: 'all', label: 'Todas' },
  { id: 'unseen', label: 'Nuevas' },
  { id: 'seen', label: 'Vistas' },
];

function readText(value) {
  return String(value || '').trim();
}

function normalizeSearchText(value) {
  return readText(value).toLowerCase();
}

/**
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function isCollectionInquiryUnseen(row) {
  return !readText(row?.seen_at);
}

/**
 * Snapshot del lote al enviar. No se rehidrata.
 *
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getCollectionInquiryItemLabel(row) {
  return readText(row?.item_label) || 'Lote';
}

/**
 * Destino para abrir la ficha desde la bandeja.
 *
 * @param {object|null|undefined} row
 * @returns {{ itemType: string, lotId?: string, userLotId?: string, parcelId?: string, label: string }|null}
 */
export function getCollectionInquiryOpenTarget(row) {
  const label = getCollectionInquiryItemLabel(row);
  const kind = readText(row?.collection_kind);
  const parcelId = readText(row?.parcel_id);
  if (kind === 'cad' || parcelId) {
    if (!parcelId) return null;
    return { itemType: 'custom_parcel', parcelId, label };
  }
  const userLotId = readText(row?.user_lot_id);
  if (userLotId) {
    return {
      itemType: 'unit',
      lotId: readText(row?.lot_id) || undefined,
      userLotId,
      label,
    };
  }
  const lotId = readText(row?.lot_id);
  if (lotId) return { itemType: 'lot', lotId, userLotId: null, label };
  return null;
}

/**
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getCollectionInquirySearchText(row) {
  return normalizeSearchText([
    row?.visitor_name,
    row?.visitor_email,
    row?.visitor_phone,
    row?.message,
    getCollectionInquiryItemLabel(row),
  ].filter(Boolean).join(' '));
}

/**
 * @param {object[]} rows
 * @param {{ query?: string, seen?: 'all'|'unseen'|'seen' }} [filters]
 * @returns {object[]}
 */
export function filterCollectionInquiryRows(rows, filters = {}) {
  const query = normalizeSearchText(filters.query);
  const seen = filters.seen || 'all';
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (seen === 'unseen' && !isCollectionInquiryUnseen(row)) return false;
    if (seen === 'seen' && isCollectionInquiryUnseen(row)) return false;
    if (query && !getCollectionInquirySearchText(row).includes(query)) return false;
    return true;
  });
}

/**
 * Pie de la consulta abierta. Siempre con fecha: “Enviado el 10/09 · 11:43”.
 *
 * @param {string} value
 * @param {Date} [now]
 * @returns {string}
 */
function pad2(value) {
  return String(value).padStart(2, '0');
}

export function formatCollectionInquirySentLabel(value, now = new Date()) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime()) || !Number.isFinite(now.getTime())) return '';
  const time = date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  const day = `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}`;
  if (date.getFullYear() !== now.getFullYear()) {
    return `Enviado el ${day}/${date.getFullYear()} · ${time}`;
  }
  return `Enviado el ${day} · ${time}`;
}

/**
 * @param {string} email
 * @returns {string}
 */
export function getCollectionInquiryMailto(email) {
  const value = readText(email);
  return value ? `mailto:${value}` : '';
}

/**
 * @param {string} phone
 * @returns {string}
 */
export function getCollectionInquiryTelHref(phone) {
  const value = readText(phone);
  if (!value) return '';
  const compact = value.replace(/[^\d+]/g, '');
  return compact ? `tel:${compact}` : '';
}

/**
 * @param {object|null|undefined} row
 * @param {Date} [now]
 * @returns {{
 *   id: string,
 *   visitorName: string,
 *   visitorEmail: string,
 *   visitorPhone: string,
 *   message: string,
 *   itemLabel: string,
 *   mailSent: boolean,
 *   unseen: boolean,
 *   time: string,
 *   timeShort: string,
 *   sentLabel: string,
 *   manual: boolean,
 *   originTags: string[],
 * }}
 * Fecha = `occurred_at ?? created_at` (manual: cuándo consultó). Manual:
 * pie “Consulta del … · cargada a mano” (nunca manda mail).
 */
export function formatCollectionInquiryRow(row, now = new Date()) {
  const at = getInquiryDisplayAt(row);
  const manual = isManualInquiry(row);
  const sent = formatCollectionInquirySentLabel(at, now);
  return {
    id: readText(row?.id),
    visitorName: readText(row?.visitor_name) || 'Sin nombre',
    visitorEmail: readText(row?.visitor_email),
    visitorPhone: readText(row?.visitor_phone),
    message: readText(row?.message),
    itemLabel: getCollectionInquiryItemLabel(row),
    mailSent: row?.mail_sent === true,
    unseen: isCollectionInquiryUnseen(row),
    time: formatCollectionActivityTime(at),
    timeShort: formatCollectionActivityClock(at, now),
    sentLabel: manual && sent ? `${sent.replace(/^Enviado el/, 'Consulta del')} · cargada a mano` : sent,
    manual,
    originTags: getManualInquiryTags(row),
  };
}
